import {
  BellIcon,
  CalendarIcon,
  ClockIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import {
  ErrorMessage,
  Field,
  FieldProps,
  Form,
  Formik,
  FormikConfig,
  FormikProps,
} from 'formik';
import React from 'react';
import { Button, Modal } from 'react-daisyui';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context';
import { tOption } from '../../../../Agenda.types';
import { allowedFieldsError, durationOptions } from '../../Calendar.constants';
import { getAllowedFields } from '../../Calendar.functions';
import { initialValues, useCalendarContext } from '../../Calendar.hook';
import { tUpdateEventForm } from '../../Calendar.types';
import CustomSelect from '../../components/CustomSelect.component';
import styles from './UpdateEvent.module.scss';
import { UpdateEventSchema } from './UpdateEvent.schema';

export default function UpdateEvent(): React.ReactElement {
  const { t } = useTranslation();
  const { workspaceInfo } = useWorkspaceContext();
  const {
    showUpdateEvent,
    setShowUpdateEvent,
    onUpdateEventSubmit,
    templates,
    currentArg,
    setSelectedEvent,
  } = useCalendarContext();

  const currentEventArg = currentArg?.event?.extendedProps?.data;
  const workspaceUsersOptions: tOption[] =
    Object.values(workspaceInfo?.users ?? {}).map((user) => ({
      value: user.authId,
      label: user.name,
    })) || [];
  const defaultOptions =
    workspaceUsersOptions.filter((user) =>
      currentEventArg?.guests?.includes(user.value),
    ) || [];
  const formikConfig: FormikConfig<tUpdateEventForm> = {
    initialValues: {
      id: currentArg?.event?.id || '',
      ...initialValues,
      ...currentEventArg,
      guests: defaultOptions,
    },
    onSubmit: onUpdateEventSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: UpdateEventSchema,
  };

  return (
    <Modal.Legacy
      open={showUpdateEvent}
      onClickBackdrop={() => {
        setSelectedEvent(undefined);
        setShowUpdateEvent(false);
      }}
      className="bg-base-100 max-w-screen-sm no-scrollbar p-0 rounded-md"
    >
      <Formik {...formikConfig}>
        {(formikProps: FormikProps<tUpdateEventForm>) => {
          const { setFieldValue, resetForm, values, touched, isValid } =
            formikProps;
          const allowedFields = getAllowedFields(templates, values);

          const isDisabledButton = (() => {
            if (!isValid) return true;
            if (allowedFields.description && !values.description) return true;
            if (allowedFields.address && !values.address) return true;
            if (allowedFields.link && !values.link) return true;
            if (allowedFields.notes && !values.note) return true;
            if (allowedFields.guests && !values.guests) return true;

            return false;
          })();

          const handleOnClose = () => {
            setSelectedEvent(undefined);
            setShowUpdateEvent(false);
            resetForm();
          };

          const isValidDescription = touched.description && !values.description;
          const isValidAddress = touched.address && !values.address;
          const isValidLink = touched.link && !values.link;
          const isValidNote = touched.note && !values.note;
          const isValidGuests = touched.guests && !values.guests;

          return (
            <Form className={styles.UpdateEvent}>
              <h1 className={styles.UpdateEventTitle}>
                {t('agenda/agenda:form-update-event-title')}
              </h1>

              <div className={styles.InputWrapper}>
                <label htmlFor="templateId" className={styles.Label}>
                  {t('agenda/agenda:what-event-type')}
                </label>

                <div className={styles.InputBox}>
                  <BellIcon className="w-6" />
                  <Field name="templateId" id="templateId">
                    {({ field }: FieldProps<string>) => (
                      <select {...field} className={styles.Select}>
                        <option value="" className="flex gap-2">
                          {t('agenda/agenda:select-a-template')}
                        </option>
                        {templates?.map((template) => (
                          <option value={template.id}>
                            {String.fromCodePoint(parseInt(template.emoji, 16))}{' '}
                            {template.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                </div>

                <ErrorMessage
                  name="templateId"
                  component="div"
                  className={styles.ErrorMessage}
                />
              </div>

              <div className={styles.InputWrapper}>
                <label htmlFor="title" className={styles.Label}>
                  {t('agenda/agenda:add-a-title')}
                </label>
                <div className={styles.InputBox}>
                  <img
                    src="/images/agenda/MegaPhoneIcon.svg"
                    alt="bell icon"
                    className="w-6"
                  />
                  <Field
                    type="text"
                    className={styles.Input}
                    placeholder="Ingresa el título del evento"
                    name="title"
                  />
                </div>

                <ErrorMessage
                  name="title"
                  component="div"
                  className={styles.ErrorMessage}
                />
              </div>

              <div className={styles.InputWrapperFlex}>
                <div className="w-1/2">
                  <div>
                    <label htmlFor="startDate" className={styles.Label}>
                      {t('agenda/agenda:select-a-date')}
                    </label>

                    <div className={styles.InputBox}>
                      <CalendarIcon className="w-6" />
                      <Field className={styles.Input} name="startDate">
                        {({ field }: FieldProps) => (
                          <DatePicker
                            className={styles.Input}
                            selected={
                              (field.value && new Date(field.value)) ||
                              new Date()
                            }
                            popperClassName="ReactDatepicker_Popper"
                            wrapperClassName="ReactDatepicker_Wrapper"
                            onChange={(val) => setFieldValue(field.name, val)}
                            // minDate={new Date()}
                          />
                        )}
                      </Field>
                    </div>
                    <ErrorMessage
                      name="startDate"
                      component="div"
                      className={styles.ErrorMessage}
                    />
                  </div>
                </div>

                <div className="w-1/2">
                  <div>
                    <label htmlFor="time" className={styles.Label}>
                      {t('agenda/agenda:select-an-hour')}
                    </label>

                    <div className={styles.InputBox}>
                      <ClockIcon className="w-6" />
                      <Field name="time">
                        {({ field }: FieldProps) => (
                          <DatePicker
                            selected={field.value}
                            popperClassName="ReactDatepicker_Popper"
                            wrapperClassName="ReactDatepicker_Wrapper"
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="h:mm aa"
                            onChange={(val) => setFieldValue(field.name, val)}
                            minDate={new Date()}
                          />
                        )}
                      </Field>
                    </div>

                    <ErrorMessage
                      name="time"
                      component="div"
                      className={styles.ErrorMessage}
                    />
                  </div>
                </div>
              </div>

              {!!allowedFields.description && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="description" className={styles.Label}>
                    {t('agenda/agenda:write-a-description')}
                  </label>
                  <div className={styles.InputBox}>
                    <img
                      src="/images/agenda/ChatBubbleIcon.svg"
                      alt="bell icon"
                      className="w-6"
                    />
                    <Field
                      type="text"
                      name="description"
                      className={styles.Input}
                      placeholder="Ej. Llamar al cliente para recordar de las promociones"
                    />
                  </div>

                  {isValidDescription && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="description"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              <div className={styles.InputWrapper}>
                <label htmlFor="duration" className={styles.Label}>
                  {t('agenda/agenda:event-duration')}
                </label>

                <div className={styles.InputBox}>
                  <ClockIcon className="w-6" />
                  <Field className={styles.Input} name="duration" id="duration">
                    {({ field }: FieldProps<string>) => (
                      <select {...field} className={styles.Select}>
                        <option value="">Selecciona una opción</option>
                        {durationOptions.map((option) => (
                          <option value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    )}
                  </Field>
                </div>
                <ErrorMessage
                  name="duration"
                  component="div"
                  className={styles.ErrorMessage}
                />
              </div>

              {!!allowedFields.address && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="address" className={styles.Label}>
                    {t('agenda/agenda:add-address')}
                  </label>

                  <div className={styles.InputBox}>
                    <img
                      src="/images/agenda/MapPinIcon.svg"
                      alt="bell icon"
                      className="w-6"
                    />
                    <Field
                      type="text"
                      name="address"
                      className={styles.Input}
                      placeholder="Ej. Monte Alban 304, Int. 5"
                    />
                  </div>

                  {isValidAddress && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="address"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              {!!allowedFields.links && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="link" className={styles.Label}>
                    {t('agenda/agenda:add-link')}
                  </label>
                  <div className={styles.InputBox}>
                    <LinkIcon className="w-5" />
                    <Field
                      type="text"
                      name="link"
                      className={styles.Input}
                      placeholder="Ej. http://www.ejemplo.com"
                    />
                  </div>

                  {isValidLink && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="link"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              {!!allowedFields.users && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="guests" className={styles.Label}>
                    {t('agenda/agenda:want-to-invite')}
                  </label>

                  <Field
                    name="guests"
                    component={CustomSelect}
                    options={workspaceUsersOptions}
                    isMulti
                    defaultOptions={defaultOptions}
                  />

                  {isValidGuests && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="guests"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              {!!allowedFields.notes && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="note" className={styles.Label}>
                    {t('agenda/agenda:want-to-add-a-note')}
                  </label>

                  <div className={styles.InputBox}>
                    <img
                      src="/images/agenda/ChatBubbleIcon.svg"
                      alt="bell icon"
                      className="w-6"
                    />
                    <Field
                      type="text"
                      name="note"
                      className={styles.Input}
                      placeholder="Ej. Mencionar las futuras promociones."
                    />
                  </div>

                  {isValidNote && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="note"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              <div className={styles.FormOptions}>
                <Button
                  color="ghost"
                  variant="outline"
                  type="button"
                  onClick={handleOnClose}
                >
                  {t('agenda/agenda:cancel-button')}
                </Button>

                <Button
                  color="primary"
                  type="submit"
                  disabled={isDisabledButton}
                >
                  {t('agenda/agenda:save-button')}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal.Legacy>
  );
}
